import { ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ErrorCodes, type BaseError, MetaErrorCodes, type Meta } from '@cplug/frontend-core/models'
import { BaseCRUDStore } from '@cplug/frontend-core/stores'
import { type LegalTerm } from '../models/LegalTerm'
import { isValidLegalTermExternalLink, isLegalTermTextEmpty } from '../composables/useLegalTermHelpers'
import { CPLegalTermGateway } from '../gateways/CPLegalTermGateway'

class LegalTermStore extends BaseCRUDStore<LegalTerm> {
  protected gateway = CPLegalTermGateway.instance()

  protected findErrors (model: LegalTerm): BaseError[] {
    const defaultErrors = (meta: Meta): BaseError => ({ code: ErrorCodes.DataValidation, meta })
    const errors: BaseError[] = []
    const hasExternalLink = (model.externalLink?.trim().length ?? 0) > 0
    const hasText = !isLegalTermTextEmpty(model.text)

    if (hasExternalLink) {
      if ((model.externalLink?.trim().length ?? 0) <= 0) {
        errors.push(defaultErrors({ externalLink: MetaErrorCodes.Empty }))
      } else if (!isValidLegalTermExternalLink(model.externalLink!)) {
        errors.push(defaultErrors({ externalLink: MetaErrorCodes.WrongFormat }))
      }
    } else if (!hasText) {
      errors.push(defaultErrors({ text: MetaErrorCodes.Empty }))
    }

    return errors
  }
}

const useLegalTermStore = defineStore('apps/legal-term', () => {
  const legalTermStore = new LegalTermStore()
  const crudStore = legalTermStore.defaultStore()

  const legalTermsByApp = ref<LegalTerm[]>([])
  const loadingByApp = ref(false)
  const loadedAppId = ref<number | null>(null)
  let fetchByAppPromise: Promise<void> | null = null
  let fetchByAppGeneration = 0

  const templates = ref<LegalTerm[]>([])
  const loadingTemplates = ref(false)
  const templatesError = ref<BaseError | null>(null)

  function fetchByApp (appId: number, force = false): Promise<void> {
    if (!force && loadedAppId.value === appId) {
      return Promise.resolve()
    }

    if (fetchByAppPromise && !force) {
      return fetchByAppPromise
    }

    const generation = ++fetchByAppGeneration

    fetchByAppPromise = (async (): Promise<void> => {
      loadingByApp.value = true

      try {
        const result = await CPLegalTermGateway.instance().listByApp(appId)

        result.ifSuccess((pageList) => {
          if (generation !== fetchByAppGeneration) {
            return
          }

          legalTermsByApp.value = pageList.contentList
          loadedAppId.value = appId
        })
      } finally {
        if (generation === fetchByAppGeneration) {
          loadingByApp.value = false
          fetchByAppPromise = null
        }
      }
    })()

    return fetchByAppPromise
  }

  async function fetchTemplates (): Promise<boolean> {
    loadingTemplates.value = true
    templatesError.value = null

    const result = await CPLegalTermGateway.instance().list(1, 100)

    let success = false
    result.ifSuccess((pageList) => {
      templates.value = pageList.contentList
      success = true
    })
    result.ifError((error) => {
      templatesError.value = error
    })

    loadingTemplates.value = false
    return success
  }

  async function save (model: LegalTerm): Promise<boolean> {
    const success = await crudStore.save(model)

    if (success) {
      loadedAppId.value = null
      fetchByAppPromise = null
      fetchByAppGeneration++
    }

    return success
  }

  function resetCache (): void {
    legalTermsByApp.value = []
    loadingByApp.value = false
    templates.value = []
    loadingTemplates.value = false
    templatesError.value = null
    loadedAppId.value = null
    fetchByAppPromise = null
    fetchByAppGeneration++
  }

  return {
    ...crudStore,
    legalTermsByApp,
    loadingByApp,
    fetchByApp,
    templates,
    loadingTemplates,
    templatesError,
    fetchTemplates,
    save,
    resetCache
  }
})

export default useLegalTermStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useLegalTermStore, (import.meta as any).hot))
}
